import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileText, Users, TrendingUp, Clock, ChevronDown, ChevronRight,
  Sparkles, CheckCircle2, Euro, Calendar, RefreshCw, Edit2, Check, X,
  AlertTriangle, BarChart2, Activity, Cpu, ChevronUp, Download, Loader2,
  Wrench, Newspaper, MapPin, Truck, Plus, Trash2, Zap, BrainCircuit,
  FileBarChart, ArrowRight, Circle, CalendarRange
} from "lucide-react";

const AUTO_REFRESH_SECS = 300; // 5 minutes

/* ── Date Range Picker ──────────────────────────────────── */
function DateRangePicker({ dateFrom, dateTo, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isSet = dateFrom || dateTo;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
          isSet ? "bg-[#007934]/10 border-[#007934]/30 text-[#007934] font-semibold" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
        }`}
        data-testid="date-range-picker-btn"
      >
        <CalendarRange size={13} />
        {isSet ? `${dateFrom || "…"} → ${dateTo || "…"}` : "Período"}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 bg-white rounded-xl shadow-xl border border-gray-100 p-4 w-72">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Filtrar período del informe</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Fecha inicio</label>
              <input type="date" value={dateFrom || ""} onChange={e => onChange("from", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Fecha fin</label>
              <input type="date" value={dateTo || ""} onChange={e => onChange("to", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
          </div>
          <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
            {isSet && (
              <button onClick={() => { onClear(); setOpen(false); }}
                className="flex-1 text-xs text-gray-500 border border-gray-200 rounded-lg py-1.5 hover:bg-gray-50">
                Limpiar
              </button>
            )}
            <button onClick={() => setOpen(false)}
              className="flex-1 text-xs text-white bg-[#007934] rounded-lg py-1.5 font-semibold hover:bg-[#006228]">
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_LABELS = {
  draft: { label: "Borrador", cls: "bg-gray-100 text-gray-600" },
  pending_it_validation: { label: "Validación TIC", cls: "bg-yellow-100 text-yellow-700" },
  pending_cfo_approval: { label: "Aprobación CFO", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Aprobada", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazada", cls: "bg-red-100 text-red-700" },
};

const SEMAFORO = {
  Verde: { cls: "bg-green-500", text: "text-green-700", bg: "bg-green-50 border-green-200", label: "En Objetivo" },
  Amarillo: { cls: "bg-yellow-400", text: "text-yellow-700", bg: "bg-yellow-50 border-yellow-200", label: "En Riesgo" },
  Rojo: { cls: "bg-red-500", text: "text-red-700", bg: "bg-red-50 border-red-200", label: "Crítico" },
};

const TYPE_BADGE = { dotnet: "bg-blue-100 text-blue-700", gis: "bg-teal-100 text-teal-700", default: "bg-gray-100 text-gray-600" };

const fmt = (n) => (n || 0).toLocaleString("es-ES", { minimumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

function KPICard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accent}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 font-['Outfit'] leading-tight">{value}</p>
        <p className="text-xs text-gray-500 leading-tight">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function EditableCell({ value, onSave, suffix = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const save = async () => {
    setEditing(false);
    if (val !== (value ?? "")) await onSave(Number(val));
  };
  if (editing) {
    return (
      <span className="flex items-center gap-1">
        <input autoFocus type="number" value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          className="w-20 border border-[#007934] rounded px-1.5 py-0.5 text-xs focus:outline-none" />
        <button onClick={save} className="text-green-600"><Check size={12} /></button>
        <button onClick={() => setEditing(false)} className="text-gray-400"><X size={12} /></button>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 group cursor-pointer" onClick={() => setEditing(true)}>
      <span className={`font-medium ${value ? "text-gray-900" : "text-gray-300 italic"}`}>
        {value ? `${fmt(value)} ${suffix}` : "—"}
      </span>
      <Edit2 size={10} className="text-gray-300 group-hover:text-[#007934] transition-colors" />
    </span>
  );
}

/* ── AI Analysis Modal ──────────────────────────────────── */
function AIModal({ title, analysis, onClose }) {
  if (!analysis) return null;
  const sem = SEMAFORO[analysis.semaforo_global || analysis.estado_general] || SEMAFORO.Verde;
  const isGlobal = !!analysis.resumen_ejecutivo;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="ai-modal">
        {/* Header */}
        <div className={`p-5 rounded-t-2xl border-b ${sem.bg}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-3 h-3 rounded-full ${sem.cls} flex-shrink-0`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${sem.text}`}>{sem.label}</span>
              </div>
              <h3 className="font-bold text-gray-900 text-lg leading-tight">{title}</h3>
              {analysis.generated_at && (
                <p className="text-xs text-gray-400 mt-1">
                  Generado: {new Date(analysis.generated_at).toLocaleString("es-ES")}
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={20} /></button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Summary */}
          <div>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              {isGlobal ? "Resumen Ejecutivo" : "Resumen"}
            </h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              {analysis.resumen_ejecutivo || analysis.resumen}
            </p>
          </div>

          {/* 2-col layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Risks / Projects at risk */}
            {(analysis.riesgos || analysis.proyectos_en_riesgo || []).length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-red-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <AlertTriangle size={12} /> {isGlobal ? "Proyectos en riesgo" : "Riesgos"}
                </h4>
                <ul className="space-y-1">
                  {(analysis.riesgos || analysis.proyectos_en_riesgo || []).map((r, i) => (
                    <li key={`riesgo-${i}`} className="text-xs text-gray-600 flex gap-1.5">
                      <span className="text-red-400 mt-0.5">•</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {(analysis.recomendaciones || analysis.recomendaciones_comite || []).length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-[#007934] uppercase tracking-wider mb-2 flex items-center gap-1">
                  <CheckCircle2 size={12} /> Recomendaciones
                </h4>
                <ul className="space-y-1">
                  {(analysis.recomendaciones || analysis.recomendaciones_comite || []).map((r, i) => (
                    <li key={`rec-${i}`} className="text-xs text-gray-600 flex gap-1.5">
                      <span className="text-[#007934] mt-0.5">→</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Logros (global only) */}
          {isGlobal && (analysis.logros_recientes || []).length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Logros Recientes</h4>
              <ul className="space-y-1">
                {analysis.logros_recientes.map((l, i) => (
                  <li key={`logro-${i}`} className="text-xs text-gray-600 flex gap-1.5"><span className="text-blue-400">✓</span>{l}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Next milestones / completion estimate */}
          {(analysis.proximos_hitos || analysis.estimacion_finalizacion) && (
            <div className="bg-gray-50 rounded-xl p-3">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Calendar size={12} /> {isGlobal ? "Próximos hitos" : "Estimación de finalización"}
              </h4>
              {analysis.estimacion_finalizacion && (
                <p className="text-sm font-semibold text-gray-800">{analysis.estimacion_finalizacion}</p>
              )}
              {(analysis.proximos_hitos || []).map((h, i) => (
                <p key={`hito-${i}`} className="text-xs text-gray-600">• {h}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Active Projects Section ─────────────────────────────── */
function ActiveProjectsSection({ projects, avgRate, avgRateIsFallback, onRefresh, onEditProject }) {
  const [analyzing, setAnalyzing] = useState({});
  const [analyzingGlobal, setAnalyzingGlobal] = useState(false);
  const [modal, setModal] = useState(null); // { title, analysis }
  const [globalSummary, setGlobalSummary] = useState(null);
  const [showGlobal, setShowGlobal] = useState(false);

  const handleProjectAI = async (pid, title) => {
    setAnalyzing(prev => ({ ...prev, [pid]: true }));
    try {
      const { data } = await axios.post(`${API}/committee/projects/${pid}/ai-analysis`);
      setModal({ title, analysis: data });
      onRefresh();
    } catch { toast.error("Error al generar análisis Pando"); }
    finally { setAnalyzing(prev => ({ ...prev, [pid]: false })); }
  };

  const handleGlobalAI = async () => {
    setAnalyzingGlobal(true);
    try {
      const { data } = await axios.post(`${API}/committee/projects/ai-summary`);
      setGlobalSummary(data);
      setShowGlobal(true);
    } catch { toast.error("Error al generar resumen global"); }
    finally { setAnalyzingGlobal(false); }
  };

  const totalWI = projects.reduce((s, p) => s + p.total_wi, 0);
  const totalDone = projects.reduce((s, p) => s + p.done_wi, 0);
  const totalHoursRem = projects.reduce((s, p) => s + p.hours_remaining, 0);
  const totalCostRem = projects.reduce((s, p) => s + p.cost_remaining, 0);
  // R10 (auditoría externa) · cuántas tareas abiertas de TODA la cartera
  // no tienen ninguna hora real registrada (remaining_work/estimated_hours/
  // ai_hours/story_points vacíos) y caen en un valor por defecto según tipo
  // — para no presentar "coste pendiente" como si fuera 100% dato medido.
  const totalItemsWithoutEstimate = projects.reduce((s, p) => s + (p.items_without_real_estimate || 0), 0);

  return (
    <div className="space-y-3">
      {/* Section header + global AI button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-[#007934] rounded-full" />
          <h3 className="font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <Activity size={16} className="text-[#007934]" />
            Proyectos Activos
          </h3>
          <span className="text-xs text-gray-400">
            Tarifa media: <strong className="text-gray-700">{fmt(avgRate)}€/h</strong>
            {avgRateIsFallback && (
              <span className="ml-1 text-amber-600 font-medium" data-testid="avg-rate-fallback-flag" title="Ningún recurso tiene tarifa real cargada — este valor es una estimación de respaldo, no un promedio calculado de datos reales">
                (estimada, sin tarifas reales cargadas)
              </span>
            )}
          </span>
          {totalItemsWithoutEstimate > 0 && (
            <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5" data-testid="total-items-without-estimate">
              {totalItemsWithoutEstimate} tareas abiertas sin estimación real (hora por defecto según tipo)
            </span>
          )}
        </div>
        <button
          onClick={handleGlobalAI}
          disabled={analyzingGlobal}
          className="flex items-center gap-2 bg-[#007934] hover:bg-[#006528] text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-60"
          data-testid="global-ai-summary-btn"
        >
          {analyzingGlobal ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {analyzingGlobal ? "Generando..." : "Análisis Global Pando"}
        </button>
      </div>

      {/* Global summary (when available) */}
      {showGlobal && globalSummary && (
        <div className={`rounded-xl border p-4 ${SEMAFORO[globalSummary.semaforo_global]?.bg || "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${SEMAFORO[globalSummary.semaforo_global]?.cls || "bg-gray-400"}`} />
              <span className={`text-xs font-bold uppercase ${SEMAFORO[globalSummary.semaforo_global]?.text || "text-gray-600"}`}>
                {globalSummary.titulo || "Resumen Global Portfolio TIC"}
              </span>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setModal({ title: globalSummary.titulo || "Portfolio TIC", analysis: globalSummary })}
                className="text-xs text-gray-500 hover:text-gray-700 underline">Ver detalle</button>
              <button onClick={() => setShowGlobal(false)} className="text-gray-400 ml-2"><X size={14} /></button>
            </div>
          </div>
          <p className="text-sm text-gray-700 mt-2 leading-relaxed line-clamp-2">{globalSummary.resumen_ejecutivo}</p>
        </div>
      )}

      {/* Projects table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Totals row */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex gap-6 text-xs text-gray-500">
          <span><strong className="text-gray-800">{projects.length}</strong> proyectos</span>
          <span><strong className="text-gray-800">{totalWI}</strong> WIs totales</span>
          <span><strong className="text-gray-800">{totalDone}</strong> completados</span>
          <span><strong className="text-gray-800">{fmt(totalHoursRem)}h</strong> restantes</span>
          <span><strong className="text-[#007934]">{fmt(totalCostRem)}€</strong> coste estimado restante</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="active-projects-table">
            <thead>
              <tr className="text-xs text-gray-400 uppercase border-b border-gray-50 bg-gray-50/30">
                <th className="text-left px-4 py-3 font-semibold">Proyecto</th>
                <th className="text-center px-3 py-3 font-semibold">WIs</th>
                <th className="text-center px-3 py-3 font-semibold">Avance</th>
                <th className="text-center px-3 py-3 font-semibold">H. Estimadas</th>
                <th className="text-center px-3 py-3 font-semibold">H. Realizadas</th>
                <th className="text-center px-3 py-3 font-semibold">H. Restantes</th>
                <th className="text-right px-3 py-3 font-semibold">Coste Ejecutado</th>
                <th className="text-right px-3 py-3 font-semibold">Coste Restante</th>
                <th className="text-center px-3 py-3 font-semibold">Deadline</th>
                <th className="text-center px-3 py-3 font-semibold">Resp. Técnico</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {projects.map(p => {
                const isAnalyzing = analyzing[p.id];
                const typeBadge = TYPE_BADGE[p.type] || TYPE_BADGE.default;
                const sem = p.ai_analysis ? SEMAFORO[p.ai_analysis.estado_general || p.ai_analysis.semaforo_global] : null;
                const pctColor = p.progress >= 80 ? "bg-green-500" : p.progress >= 50 ? "bg-yellow-400" : "bg-red-400";
                return (
                  <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${p.status === "paused" ? "opacity-60" : ""}`}
                    data-testid={`project-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {sem && <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sem.cls}`} title={`Estado Pando: ${sem.label}`} />}
                        <div>
                          <p className="text-sm font-semibold text-gray-900 leading-tight">{p.title}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`text-xs px-1.5 py-0 rounded font-medium ${typeBadge}`}>{p.type?.toUpperCase()}</span>
                            {p.status === "paused" && <span className="text-xs text-gray-400 italic">Pausado</span>}
                            {p.bugs > 0 && <span className="text-xs text-red-500">{p.bugs} bugs</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <p className="text-sm font-semibold text-gray-900">{p.total_wi}</p>
                      <p className="text-xs text-gray-400">{p.done_wi} cerrados</p>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5 justify-center">
                        <div className="w-14 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${pctColor}`} style={{ width: `${p.progress}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-700">{p.progress}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-gray-600">{p.hours_total > 0 ? `${fmt(p.hours_total)}h` : "—"}</td>
                    <td className="px-3 py-3 text-center text-sm text-gray-600">{p.hours_done > 0 ? `${fmt(p.hours_done)}h` : "—"}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-sm font-medium ${p.hours_remaining > 0 ? "text-orange-600" : "text-gray-400"}`}>
                        {p.hours_remaining > 0 ? `${fmt(p.hours_remaining)}h` : "—"}
                      </span>
                      {p.items_without_real_estimate > 0 && (
                        <span
                          className="ml-1 text-[9px] text-gray-400 align-top cursor-help"
                          data-testid={`hours-no-estimate-flag-${p.id}`}
                          title={`${p.items_without_real_estimate} tareas abiertas sin horas reales registradas — ${fmt(p.hours_remaining_without_real_estimate)}h de este total es estimación por defecto, no dato medido`}
                        >
                          *
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="text-sm font-semibold text-blue-700">{p.cost_done > 0 ? `${fmt(p.cost_done)}€` : "—"}</span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="text-sm font-semibold text-orange-600">{p.cost_remaining > 0 ? `${fmt(p.cost_remaining)}€` : "—"}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs text-gray-600">{fmtDate(p.end_date)}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs text-gray-600 truncate max-w-[100px] block">{p.responsible_tech || "—"}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {p.ai_analysis && (
                          <button onClick={() => setModal({ title: p.title, analysis: p.ai_analysis })}
                            className="text-xs text-[#007934] hover:underline whitespace-nowrap" data-testid={`view-ai-${p.id}`}>
                            Ver análisis
                          </button>
                        )}
                        <button
                          onClick={() => handleProjectAI(p.id, p.title)}
                          disabled={isAnalyzing}
                          className="flex items-center gap-1 text-xs bg-[#007934]/10 hover:bg-[#007934]/20 text-[#007934] font-semibold px-2 py-1.5 rounded-lg transition-colors disabled:opacity-60 whitespace-nowrap"
                          data-testid={`analyze-ai-${p.id}`}
                        >
                          {isAnalyzing ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />}
                          {isAnalyzing ? "..." : p.ai_analysis ? "Re-analizar" : "Analizar Pando"}
                        </button>
                        {onEditProject && (
                          <button
                            onClick={() => onEditProject(p)}
                            className="p-1.5 text-gray-400 hover:text-[#007934] hover:bg-[#007934]/10 rounded-lg transition-colors"
                            data-testid={`edit-project-${p.id}`}
                            title="Editar proyecto"
                          >
                            <Edit2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Modal */}
      {modal && <AIModal title={modal.title} analysis={modal.analysis} onClose={() => setModal(null)} />}
    </div>
  );
}

/* ── Tab 1: Control de Proyectos ─────────────────────────── */
function ProjectControlTab({ offers, projects, avgRate, avgRateIsFallback, onRefresh, onEditProject }) {
  const [estimating, setEstimating] = useState({});

  const handleEstimateAI = async (oid) => {
    setEstimating(prev => ({ ...prev, [oid]: true }));
    try {
      const { data } = await axios.post(`${API}/committee/offers/${oid}/estimate-hours`);
      toast.success(`Pando estimó ${data.hours}h a ${data.rate}€/h`, { description: data.justificacion });
      onRefresh();
    } catch { toast.error("Error al generar estimación Pando"); }
    finally { setEstimating(prev => ({ ...prev, [oid]: false })); }
  };

  const handleSaveHours = async (oid, hours) => {
    try { await axios.put(`${API}/offers/${oid}`, { dev_hours: hours }); onRefresh(); }
    catch { toast.error("Error al guardar"); }
  };

  const handleSaveRate = async (oid, rate) => {
    try { await axios.put(`${API}/offers/${oid}`, { dev_rate: rate }); onRefresh(); }
    catch { toast.error("Error al guardar"); }
  };

  const totalDevHours = offers.reduce((s, o) => s + (o.dev_hours || 0), 0);
  const totalCapex = offers.reduce((s, o) => s + (o.capex_total || 0), 0);
  const totalOpex = offers.reduce((s, o) => s + (o.opex_total || 0), 0);
  const totalCostRem = projects.reduce((s, p) => s + (p.cost_remaining || 0), 0);

  return (
    <div className="space-y-6">
      {/* Top KPIs combining offers + projects */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Ofertas en pipeline" value={offers.length} icon={FileText} accent="bg-[#007934]" />
        <KPICard label="CAPEX total (prelicitación)" value={`${fmt(totalCapex)}€`} icon={Euro} accent="bg-blue-600" />
        <KPICard label="Proyectos activos" value={projects.filter(p => p.status === "active").length} sub={`${projects.length} total`} icon={Activity} accent="bg-indigo-600" />
        <KPICard label="Coste restante estimado" value={`${fmt(totalCostRem)}€`} sub="proyectos activos" icon={TrendingUp} accent="bg-orange-500" />
      </div>

      {/* Section 1: Proyectos Activos */}
      <ActiveProjectsSection projects={projects} avgRate={avgRate} avgRateIsFallback={avgRateIsFallback} onRefresh={onRefresh} onEditProject={onEditProject} />

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Nuevas Licitaciones en Prelicitación</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Section 2: Offers */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <FileText size={16} className="text-[#007934]" />
            Ofertas y Acuerdos CAPEX/OPEX
          </h3>
          <span className="text-xs text-gray-400">Clic en Horas/Tarifa para editar</span>
        </div>
        {offers.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No hay ofertas registradas</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="committee-offers-table">
              <thead>
                <tr className="text-xs text-gray-400 uppercase border-b border-gray-50 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-semibold">Oferta / Licitación</th>
                  <th className="text-left px-3 py-3 font-semibold">Municipio</th>
                  <th className="text-left px-3 py-3 font-semibold">Resp. Negocio</th>
                  <th className="text-right px-3 py-3 font-semibold">CAPEX</th>
                  <th className="text-right px-3 py-3 font-semibold">OPEX/año</th>
                  <th className="text-center px-3 py-3 font-semibold">H. Desarrollo</th>
                  <th className="text-center px-3 py-3 font-semibold">Tarifa €/h</th>
                  <th className="text-center px-3 py-3 font-semibold">Estado</th>
                  <th className="text-center px-3 py-3 font-semibold">Deadline</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {offers.map(o => {
                  const st = STATUS_LABELS[o.status] || { label: o.status, cls: "bg-gray-100 text-gray-600" };
                  return (
                    <tr key={o.id} className="hover:bg-gray-50 transition-colors" data-testid={`offer-row-${o.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900 leading-tight">{o.title}</p>
                        <p className="text-xs text-gray-400">{o.licitacion || o.contract_type}</p>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">{o.municipality || "—"}</td>
                      <td className="px-3 py-3 text-sm text-gray-600">{o.responsible_business || "—"}</td>
                      <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-blue-700">{fmt(o.capex_total)}€</span></td>
                      <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-teal-700">{fmt(o.opex_total)}€</span></td>
                      <td className="px-3 py-3 text-center">
                        <EditableCell value={o.dev_hours} suffix="h" onSave={(v) => handleSaveHours(o.id, v)} />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <EditableCell value={o.dev_rate} suffix="€/h" onSave={(v) => handleSaveRate(o.id, v)} />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-3 py-3 text-center text-xs text-gray-600">{fmtDate(o.deadline)}</td>
                      <td className="px-3 py-3">
                        {!o.dev_hours && (
                          <button onClick={() => handleEstimateAI(o.id)} disabled={estimating[o.id]}
                            className="flex items-center gap-1 text-xs bg-[#007934] text-white px-2 py-1.5 rounded-lg disabled:opacity-60 whitespace-nowrap"
                            data-testid={`estimate-ai-${o.id}`}>
                            {estimating[o.id] ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />}
                            Estimar Pando
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Tab 2: Asignación de Personas ──────────────────────── */
function PeopleAssignmentTab({ people, periodDays = 10, dateFrom, dateTo, onAIHours, aiHoursLoading }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const nearestDeadline = (projects) => {
    const dates = projects.map(p => p.deadline).filter(Boolean).sort();
    return dates[0] ? fmtDate(dates[0]) : "—";
  };

  // T11 "Alertas de Copiloto" (tanda integral 12/10/2026) · deep-link desde
  // Mi Trabajo a una nota person_activity concreta — hay que expandir primero
  // la fila de la persona dueña (acordeón) antes de poder hacer scroll a ella.
  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get("open");
    if (!openId) return;
    const owner = people.find((p) => (p.activities || []).some((a) => a.id === openId));
    if (!owner) return;
    setExpanded((prev) => ({ ...prev, [owner.id]: true }));
    const t = setTimeout(() => {
      const el = document.getElementById(`act-${openId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber-500");
        setTimeout(() => el.classList.remove("ring-2", "ring-amber-500"), 4000);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [people]);

  const periodLabel = periodDays === 0
    ? (dateFrom || dateTo) ? `${dateFrom || "…"} → ${dateTo || "…"}` : "Período personalizado"
    : `Últimos ${periodDays} días`;
  const periodCls = periodDays === 10 ? "bg-green-100 text-green-700"
    : periodDays === 0 ? "bg-blue-100 text-blue-700"
    : "bg-amber-100 text-amber-700";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPICard label="Personas en equipo" value={people.length} icon={Users} accent="bg-[#007934]" />
        <KPICard label="WIs activos" value={people.reduce((s, p) => s + (p.active_work_items || 0), 0)} sub="en todos los proyectos" icon={CheckCircle2} accent="bg-indigo-600" />
        <KPICard label="Tareas completadas" value={people.reduce((s, p) => s + p.total_done, 0)} sub="cerradas" icon={TrendingUp} accent="bg-teal-600" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <Users size={16} className="text-[#007934]" /> Asignación de Personas — Proyectos Activos
            <span className={`ml-1 text-xs font-semibold px-2 py-0.5 rounded-full ${periodCls}`}>
              {periodLabel}
            </span>
          </h3>
          <button
            onClick={onAIHours}
            disabled={aiHoursLoading}
            data-testid="ai-estimate-hours-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-60"
            title="Usar Pando para estimar horas de work items sin datos"
          >
            {aiHoursLoading ? <Loader2 size={12} className="animate-spin" /> : <BrainCircuit size={12} />}
            {aiHoursLoading ? "Estimando…" : "Pando Horas"}
          </button>
        </div>
        <div className="divide-y divide-gray-50">
          {people.map(p => {
            const isOpen = expanded[p.id];
            const hasProjects = p.projects.length > 0 || (p.activities || []).length > 0;
            return (
              <div key={p.id} data-testid={`person-row-${p.id}`}>
                <div className={`flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors ${hasProjects ? "cursor-pointer" : ""}`}
                  onClick={() => hasProjects && toggle(p.id)}>
                  <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#007934] text-sm font-bold flex-shrink-0">
                    {p.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                      <span className="text-xs text-gray-400 hidden md:block">{p.role}</span>
                      {p.department && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 hidden md:block">{p.department}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-xs">
                        <div className="h-full bg-[#007934] rounded-full" style={{ width: `${p.completion_pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-500">{p.completion_pct}%</span>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-6 text-sm flex-shrink-0">
                    {[["WIs activos", p.active_work_items || 0], ["Completados", p.total_done], ["H. pendientes", p.pending_hours > 0 ? `${p.pending_hours}h` : "—"],
                      ["Coste est.", p.cost_estimate > 0 ? `${(p.cost_estimate).toLocaleString("es-ES")}€` : "—"],
                      ["Próx. deadline", nearestDeadline(p.projects)], ["Proyectos", p.projects.length]
                    ].map(([l, v]) => (
                      <div key={l} className="text-center"><p className={`font-semibold ${l === "Coste est." ? "text-[#007934]" : "text-gray-900"}`}>{v}</p><p className="text-xs text-gray-400">{l}</p></div>
                    ))}
                  </div>
                  {hasProjects && <div className="text-gray-400 flex-shrink-0">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>}
                </div>
                {isOpen && (
                  <div className="bg-[#f8faf9] border-t border-gray-100 px-5 py-3 space-y-3">
                    {/* DevOps projects */}
                    {p.projects.length > 0 && (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-400 uppercase">
                            {["Proyecto", "Tareas", "Hechas", "% Avance", "H. Estimadas", "Coste est.", "Deadline"].map(h => (
                              <th key={h} className="text-left py-1.5 pr-4 font-semibold">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {p.projects.map(proj => (
                            <tr key={proj.project_id} className="hover:bg-white">
                              <td className="py-2 pr-4 font-medium text-gray-800">{proj.project_name}</td>
                              <td className="py-2 pr-4 text-gray-600">{proj.tasks}</td>
                              <td className="py-2 pr-4 text-green-600 font-medium">{proj.tasks_done}</td>
                              <td className="py-2 pr-4">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-[#007934] rounded-full" style={{ width: `${proj.completion_pct}%` }} />
                                  </div>
                                  <span className={`font-medium ${proj.completion_pct >= 80 ? "text-green-600" : proj.completion_pct >= 40 ? "text-yellow-600" : "text-red-500"}`}>
                                    {proj.completion_pct}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-2 pr-4 text-gray-600">{proj.hours > 0 ? `${proj.hours}h` : "—"}</td>
                              <td className="py-2 pr-4 text-[#007934] font-medium">{proj.cost_estimate > 0 ? `${proj.cost_estimate.toLocaleString("es-ES")}€` : "—"}</td>
                              <td className="py-2">
                                {proj.deadline ? <span className="flex items-center gap-1 text-gray-600"><Calendar size={10} />{fmtDate(proj.deadline)}</span> : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {/* Manual activity notes */}
                    {(p.activities || []).length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Novedades / Actividad reciente</p>
                        {p.activities.map((act, i) => (
                          <div key={act.id || act.title || `act-${i}`} id={act.id ? `act-${act.id}` : undefined} className={`border rounded-lg p-2.5 transition-all ${act.es_antigua ? "border-gray-200 bg-gray-50 opacity-70" : "border-amber-200 bg-amber-50"}`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="text-xs font-semibold text-gray-800 leading-tight">{act.title}</p>
                              <div className="flex items-center gap-1 shrink-0">
                                {act.es_antigua && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded border font-medium bg-gray-100 text-gray-500 border-gray-300" title="Sin actualizar hace más de 60 días — no tomar como estado vigente" data-testid={`act-antigua-${act.id}`}>
                                    antigua · {act.dias_desde_evidencia}d
                                  </span>
                                )}
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${act.priority === "alta" ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>
                                  {act.priority}
                                </span>
                              </div>
                            </div>
                            <p className="text-[11px] text-gray-600 leading-relaxed whitespace-pre-line">{act.description}</p>
                            {act.project_ref && <p className="text-[10px] text-[#007934] mt-1 font-medium">{act.project_ref}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Shared NoteForm modal ────────────────────────────────── */
function NoteFormModal({ type, defaultPerson, existing, onSave, onClose }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(existing ? {
    type: existing.type, person_name: existing.person_name || defaultPerson || "",
    title: existing.title || "", description: existing.description || "",
    priority: existing.priority || "alta", status: existing.status || "active",
    project_ref: existing.project_ref || "", tags: (existing.tags || []).join(", ")
  } : {
    type, person_name: defaultPerson || "", title: "", description: "",
    priority: "alta", status: "active", project_ref: "", tags: ""
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [] };
      if (isEdit) {
        await axios.put(`${API}/committee/notes/${existing.id}`, payload);
        toast.success("Actualizado correctamente");
      } else {
        await axios.post(`${API}/committee/notes`, payload);
        toast.success("Guardado correctamente");
      }
      onSave();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">{isEdit ? "Editar novedad" : type === "commercial_news" ? "Nueva novedad comercial" : "Nueva actividad manual"}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          {type === "person_activity" && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Persona</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.person_name} onChange={e => set("person_name", e.target.value)} placeholder="Nombre completo" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Título *</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              value={form.title} onChange={e => set("title", e.target.value)} placeholder="Título de la novedad/actividad" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Descripción</label>
            <textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
              value={form.description} onChange={e => set("description", e.target.value)} placeholder="Detalle del estado o novedad..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Prioridad</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.priority} onChange={e => set("priority", e.target.value)}>
                <option value="alta">Alta</option><option value="media">Media</option><option value="baja">Baja</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Estado</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                value={form.status} onChange={e => set("status", e.target.value)}>
                <option value="active">Activo</option><option value="resolved">Resuelto</option><option value="info">Info</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Referencia de proyecto</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              value={form.project_ref} onChange={e => set("project_ref", e.target.value)} placeholder="Ej: Plataforma TIC Santa Eulàlia" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Tags (separados por coma)</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="Ej: GPS, Integración, API" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !form.title.trim()}
            className="px-4 py-2 rounded-lg text-sm bg-[#007934] text-white font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2">
            {saving && <RefreshCw size={12} className="animate-spin" />} {isEdit ? "Actualizar" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Tab: Servicios a arrancar (Novedades Comerciales) ───── */
function ServiciosArrancarTab({ commercialNews, commercialNewsAntiguas = [], onRefresh }) {
  const [expanded, setExpanded] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [statusChanging, setStatusChanging] = useState(null);
  const [autoCardsLoading, setAutoCardsLoading] = useState(false);
  const [showHistorico, setShowHistorico] = useState(() => {
    const openId = new URLSearchParams(window.location.search).get("open");
    return !!openId && commercialNewsAntiguas.some((n) => n.id === openId);
  });
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get("open");
    if (!openId) return;
    const el = document.getElementById(`service-news-${openId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-500");
      setExpanded((e) => ({ ...e, [openId]: true }));
      setTimeout(() => el.classList.remove("ring-2", "ring-amber-500"), 4000);
    }
  }, [showHistorico, commercialNews, commercialNewsAntiguas]);

  const pendingNews = commercialNews.filter(n => n.status === "active");
  const resolvedNews = commercialNews.filter(n => n.status !== "active");

  const expandAllPending = () => {
    const exp = { ...expanded };
    pendingNews.forEach(n => { exp[n.id] = true; });
    setExpanded(exp);
  };

  const handleAutoCards = async () => {
    setAutoCardsLoading(true);
    try {
      const { data } = await axios.post(`${API}/committee/ai/auto-cards`);
      const parts = [];
      if (data.updated) parts.push(`${data.updated} actualizadas`);
      if (data.created) parts.push(`${data.created} nuevas`);
      if (data.manual_protected) parts.push(`${data.manual_protected} manuales preservadas`);
      toast.success(`✓ Claude Sonnet 4.6: ${parts.join(" · ") || "sin cambios"}`);
      onRefresh();
    } catch (e) {
      toast.error("Error Pando auto-fichas: " + (e.response?.data?.detail || e.message || "desconocido"));
    } finally { setAutoCardsLoading(false); }
  };

  const handleDelete = async (nid) => {
    if (!window.confirm("¿Eliminar esta novedad?")) return;
    setDeleting(nid);
    try {
      await axios.delete(`${API}/committee/notes/${nid}`);
      toast.success("Eliminado");
      onRefresh();
    } catch { toast.error("Error al eliminar"); }
    finally { setDeleting(null); }
  };

  const handleStatusChange = async (nid, newStatus) => {
    setStatusChanging(nid);
    try {
      await axios.put(`${API}/committee/notes/${nid}`, { status: newStatus });
      onRefresh();
    } catch { toast.error("Error al actualizar estado"); }
    finally { setStatusChanging(null); }
  };

  const priorityColor = { alta: "bg-red-100 text-red-700 border-red-200", media: "bg-amber-100 text-amber-700 border-amber-200", baja: "bg-gray-100 text-gray-500 border-gray-200" };
  const statusConfig = {
    active:   { label: "En curso",  dot: "bg-amber-400", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    resolved: { label: "Resuelto",  dot: "bg-green-500", cls: "bg-green-50 text-green-700 border-green-200" },
    info:     { label: "Informativo", dot: "bg-blue-400", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  };

  const renderCard = (news) => {
    const isOpen = expanded[news.id];
    const stCfg = statusConfig[news.status] || statusConfig.active;
    const isDeleting = deleting === news.id;
    const isChanging = statusChanging === news.id;

    return (
      <div key={news.id} id={`service-news-${news.id}`} className="bg-white rounded-xl border border-gray-100 overflow-hidden transition-all" data-testid={`service-news-${news.id}`}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-start gap-3">
          <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${stCfg.dot}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-gray-900 text-sm leading-tight">{news.title}</p>
              <div className="flex items-center gap-1 flex-shrink-0">
                {(news.manually_edited || news.source === "manual") ? (
                  <span
                    className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-300"
                    title="Editada manualmente · la Pando NO la sobrescribirá"
                    data-testid={`badge-manual-${news.id}`}
                  >
                    🔒 Manual
                  </span>
                ) : news.ia_generated || news.source === "ai" ? (
                  <span
                    className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-300"
                    title={`Generada/actualizada por Pando${news.ai_model ? ` · ${news.ai_model}` : ""}`}
                    data-testid={`badge-ia-${news.id}`}
                  >
                    ✨ Pando
                  </span>
                ) : null}
                <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${priorityColor[news.priority] || priorityColor.media}`}>
                  {news.priority}
                </span>
              </div>
            </div>
            {news.project_ref && (
              <p className="text-xs text-[#007934] font-medium mt-0.5 flex items-center gap-1">
                <MapPin size={10} /> {news.project_ref}
              </p>
            )}
            {typeof news.dias_desde_evidencia === "number" && (
              <p className="text-[10px] text-gray-400 mt-0.5" data-testid={`evidencia-dias-${news.id}`}>
                Evidencia: hace {news.dias_desde_evidencia} día{news.dias_desde_evidencia === 1 ? "" : "s"}
              </p>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <button onClick={() => toggle(news.id)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-2">
            {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {isOpen ? "Ocultar detalle" : "Ver detalle"}
          </button>
          {isOpen ? (
            <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-line mb-2">{news.description}</p>
          ) : (
            <p className="text-xs text-gray-500 line-clamp-2 mb-2">{news.description}</p>
          )}
          {news.tags && news.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {news.tags.map(t => <span key={t} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{t}</span>)}
            </div>
          )}
        </div>

        {/* Footer: status + actions */}
        <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-2">
          {/* Quick status change */}
          <div className="flex items-center gap-1">
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <button
                key={key}
                disabled={news.status === key || isChanging}
                onClick={() => handleStatusChange(news.id, key)}
                className={`text-[10px] px-2 py-0.5 rounded-full border font-medium transition-all ${
                  news.status === key
                    ? `${cfg.cls} border-current`
                    : "border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600"
                }`}
                data-testid={`status-${key}-${news.id}`}
              >
                {isChanging && news.status !== key ? "..." : cfg.label}
              </button>
            ))}
          </div>
          {/* Edit / Delete / Toggle Pando */}
          <div className="flex items-center gap-1">
            {(news.manually_edited || news.source === "manual") && (
              <button
                onClick={async () => {
                  if (!window.confirm("Permitir que la Pando vuelva a gestionar esta tarjeta? Próximas regeneraciones podrán sobrescribirla.")) return;
                  try {
                    // Llamamos directamente al backend para limpiar las flags (PUT marcaría como manual otra vez)
                    await axios.put(`${API}/committee/notes/${news.id}/unmark-manual`).catch(async () => {
                      // Fallback si el endpoint no existe: usar update con flag explícito vía POST
                      await axios.post(`${API}/committee/notes/${news.id}/unmark-manual`);
                    });
                    toast.success("Tarjeta liberada · la Pando podrá actualizarla");
                    onRefresh();
                  } catch (e) {
                    toast.error("Error: " + (e.response?.data?.detail || e.message));
                  }
                }}
                className="p-1.5 text-blue-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="Permitir que la Pando gestione esta tarjeta"
                data-testid={`unmark-manual-${news.id}`}
              >
                <Sparkles size={13} />
              </button>
            )}
            <button
              onClick={() => setEditNote(news)}
              className="p-1.5 text-gray-400 hover:text-[#007934] hover:bg-[#007934]/10 rounded-lg transition-colors"
              data-testid={`edit-note-${news.id}`}
              title="Editar"
            >
              <Edit2 size={13} />
            </button>
            <button
              onClick={() => handleDelete(news.id)}
              disabled={isDeleting}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              data-testid={`delete-note-${news.id}`}
              title="Eliminar"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 flex-1 min-w-[260px]">
          <KPICard label="Servicios a arrancar" value={commercialNews.length} icon={Newspaper} accent="bg-[#007934]" />
          <KPICard label="Urgentes / Alta prioridad" value={commercialNews.filter(n => n.priority === "alta").length} icon={AlertTriangle} accent="bg-red-500" />
          <KPICard label="En curso" value={pendingNews.length} icon={Activity} accent="bg-amber-500" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {pendingNews.length > 0 && (
            <button
              onClick={expandAllPending}
              className="flex items-center gap-1.5 border border-gray-200 text-gray-600 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="expand-all-pending-btn"
            >
              <ChevronDown size={13} /> Expandir pendientes ({pendingNews.length})
            </button>
          )}
          <button
            onClick={handleAutoCards}
            disabled={autoCardsLoading}
            className="flex items-center gap-1.5 border border-purple-200 bg-purple-50 text-purple-700 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
            data-testid="ai-auto-cards-btn"
          >
            {autoCardsLoading ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
            {autoCardsLoading ? "Generando..." : "Pando Auto-actualizar"}
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#006528] transition-colors"
            data-testid="new-service-news-btn"
          >
            <Plus size={13} /> Nueva novedad
          </button>
        </div>
      </div>

      {showForm && <NoteFormModal type="commercial_news" onSave={() => { setShowForm(false); onRefresh(); }} onClose={() => setShowForm(false)} />}
      {editNote && <NoteFormModal type={editNote.type} existing={editNote} onSave={() => { setEditNote(null); onRefresh(); }} onClose={() => setEditNote(null)} />}

      {commercialNews.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-gray-400">
          <Newspaper size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay novedades comerciales registradas</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pending (active) */}
          {pendingNews.length > 0 && (
            <div>
              <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                En curso ({pendingNews.length})
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingNews.map(renderCard)}
              </div>
            </div>
          )}
          {/* Resolved / Info */}
          {resolvedNews.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block" />
                Resueltos / Informativos ({resolvedNews.length})
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {resolvedNews.map(renderCard)}
              </div>
            </div>
          )}
        </div>
      )}
      {/* P0-06 (plan cierre POC/Comité 12/10/2026) · histórico colapsado por
          defecto: notas "En curso" con evidencia de +60 días sin actualizar
          no desaparecen, pero tampoco se cuentan como vigentes por defecto. */}
      {commercialNewsAntiguas.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <button
            onClick={() => setShowHistorico((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600"
            data-testid="toggle-historico-antiguas-btn"
          >
            {showHistorico ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            Ver histórico — antiguas sin actualizar ({commercialNewsAntiguas.length})
          </button>
          <p className="text-[10px] text-gray-400 mt-1">
            Marcadas &ldquo;En curso&rdquo; pero con evidencia de más de 60 días sin actualizarse. No se cuentan en los indicadores de arriba.
          </p>
          {showHistorico && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              {commercialNewsAntiguas.map(renderCard)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Equipo Operaciones ──────────────────────────────────── */
function OpsTeamSection({ people, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoMsg, setAutoMsg] = useState("");
  const opsPeople = people.filter(p =>
    p.department === "Operaciones" || ["Jorge Tosca Ginel","Enrique Torres Sanz","Carlos Manuel Gil Lopez"].includes(p.name)
  );

  const handleAutoFill = async () => {
    setAutoFilling(true);
    setAutoMsg("");
    try {
      const { data } = await axios.post(`${API}/committee/ops/auto-fill`);
      setAutoMsg(`${data.created} nota(s) generada(s) correctamente`);
      onRefresh();
      setTimeout(() => setAutoMsg(""), 5000);
    } catch (e) {
      setAutoMsg(e.response?.data?.detail || "Error al auto-rellenar");
    } finally {
      setAutoFilling(false);
    }
  };

  const priorityColor = { alta: "bg-red-100 text-red-700 border-red-200", media: "bg-amber-100 text-amber-700 border-amber-200", baja: "bg-gray-100 text-gray-500 border-gray-200" };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wrench size={15} className="text-[#007934]" />
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Equipo Operaciones</h3>
          <span className="text-xs text-gray-400">(tareas abiertas en DevOps)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAutoFill}
            disabled={autoFilling}
            data-testid="ops-autofill-btn"
            className="flex items-center gap-1.5 bg-teal-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-60"
          >
            {autoFilling ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {autoFilling ? "Generando…" : "Auto-rellenar DevOps"}
          </button>
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#006528] transition-colors">
            <Plus size={13} /> Nueva actividad
          </button>
        </div>
      </div>
      {autoMsg && (
        <div className={`text-xs px-4 py-2 rounded-lg font-medium ${autoMsg.includes("Error") ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`} data-testid="ops-autofill-msg">
          {autoMsg}
        </div>
      )}
      {showForm && <NoteFormModal type="person_activity" onSave={() => { setShowForm(false); onRefresh(); }} onClose={() => setShowForm(false)} />}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {opsPeople.map(p => (
          <div key={p.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900 text-sm">{p.name}</p>
                <p className="text-xs text-gray-400">{p.cost_per_hour}€/h · {p.department || "Operaciones"}</p>
              </div>
              <span className="text-xs bg-[#007934]/10 text-[#007934] px-2 py-0.5 rounded-full font-medium">
                {(p.active_work_items || p.total_tasks)} WIs
              </span>
            </div>
            <div className="px-4 py-3 space-y-2">
              {/* Azure DevOps projects */}
              {p.projects.length > 0 && (
                <div className="space-y-1">
                  {p.projects.slice(0, 3).map((proj, i) => (
                    <div key={proj.project_name || `proj-${i}`} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 truncate max-w-[160px]">{proj.project_name}</span>
                      <span className="text-gray-400 shrink-0 ml-1">
                        {proj.tasks_done}/{proj.tasks} tareas ({proj.completion_pct}%)
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {/* Manual activities */}
              {(p.activities || []).map((act, i) => (
                <div key={act.id || act.title || `act-${i}`} className="border border-amber-200 bg-amber-50 rounded-lg p-2.5">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-xs font-semibold text-gray-800 leading-tight">{act.title}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 font-medium ${priorityColor[act.priority] || priorityColor.media}`}>
                      {act.priority}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-600 leading-relaxed whitespace-pre-line">{act.description}</p>
                  {act.project_ref && (
                    <p className="text-[10px] text-[#007934] mt-1 font-medium">{act.project_ref}</p>
                  )}
                </div>
              ))}
              {p.projects.length === 0 && (p.activities || []).length === 0 && (
                <p className="text-xs text-gray-400 italic">Sin asignaciones activas en los últimos 10 días</p>
              )}
            </div>
          </div>
        ))}
        {opsPeople.length === 0 && (
          <p className="text-sm text-gray-400 col-span-3">No hay recursos del equipo de Operaciones configurados</p>
        )}
      </div>
    </div>
  );
}

/* ── Project Edit Modal ─────────────────────────────────── */
function ProjectEditModal({ project, onSave, onClose }) {
  const [form, setForm] = useState({
    title: project.title || "",
    status: project.status || "active",
    priority: project.priority || "media",
    progress: project.progress ?? 0,
    responsible_tech: project.responsible_tech || "",
    responsible_business: project.responsible_business || "",
    end_date: project.end_date || "",
    description: project.description || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/committee/projects/${project.id}`, form);
      toast.success("Proyecto actualizado");
      onSave();
    } catch { toast.error("Error al actualizar proyecto"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Edit2 size={16} className="text-[#007934]" /> Editar Proyecto</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Nombre del proyecto</label>
            <input value={form.title} onChange={e => set("title", e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Estado</label>
              <select value={form.status} onChange={e => set("status", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                <option value="active">Activo</option>
                <option value="paused">Pausado</option>
                <option value="completed">Completado</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Prioridad</label>
              <select value={form.priority} onChange={e => set("priority", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Resp. Técnico</label>
              <input value={form.responsible_tech} onChange={e => set("responsible_tech", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Resp. Negocio</label>
              <input value={form.responsible_business} onChange={e => set("responsible_business", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Deadline</label>
              <input type="date" value={form.end_date} onChange={e => set("end_date", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Progreso manual (%)</label>
              <input type="number" min="0" max="100" value={form.progress} onChange={e => set("progress", Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Notas / Descripción</label>
            <textarea rows={2} value={form.description} onChange={e => set("description", e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 pt-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-[#007934] text-white rounded-lg font-semibold hover:bg-[#006228] disabled:opacity-50 flex items-center gap-1.5">
            {saving && <RefreshCw size={12} className="animate-spin" />} Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Weekly Report Panel ────────────────────────────────── */
function WeeklyReportPanel({ report, onClose }) {
  if (!report) return null;
  const sem = SEMAFORO[report.semaforo_global] || SEMAFORO.Verde;

  const SectionTitle = ({ children, color = "text-gray-500" }) => (
    <h4 className={`text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${color}`}>{children}</h4>
  );

  const trafficColor = (e) => {
    if (!e) return "";
    const k = e.toLowerCase();
    if (k.includes("rojo") || k.includes("red") || k === "alta") return "bg-red-500";
    if (k.includes("amarillo") || k.includes("yellow") || k === "media") return "bg-amber-400";
    return "bg-green-500";
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`p-5 border-b sticky top-0 z-10 ${sem.bg}`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-3 h-3 rounded-full ${sem.cls}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${sem.text}`}>
                  Semáforo global: {report.semaforo_global}
                </span>
              </div>
              <h2 className="font-bold text-gray-900 text-lg">Informe Semanal Committee</h2>
              <p className="text-xs text-gray-500">Semana: {report.semana} · Generado: {report.generado_en}</p>
            </div>
            <button onClick={onClose} className="mt-1"><X size={20} className="text-gray-500" /></button>
          </div>
          {report.resumen_ejecutivo && (
            <p className="text-sm text-gray-700 mt-3 leading-relaxed border-t border-black/5 pt-3">{report.resumen_ejecutivo}</p>
          )}
        </div>

        <div className="p-5 space-y-6">
          {/* Proyectos activos */}
          {(report.proyectos_activos || []).length > 0 && (
            <div>
              <SectionTitle color="text-[#007934]">
                <Activity size={12} className="text-[#007934]" /> Proyectos activos
              </SectionTitle>
              <div className="space-y-3">
                {report.proyectos_activos.map((p, i) => (
                  <div key={p.proyecto || p.nombre || p.title || `proj-act-${i}`} className="bg-gray-50 rounded-xl p-3.5">
                    <div className="flex items-start gap-2 mb-1.5">
                      <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${trafficColor(p.estado)}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-gray-900 text-sm">{p.titulo}</p>
                          <span className="text-xs font-bold text-gray-600">{p.progreso}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1 mb-2">
                          <div className={`h-full rounded-full ${p.progreso >= 80 ? "bg-green-500" : p.progreso >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                            style={{ width: `${p.progreso || 0}%` }} />
                        </div>
                        {p.hitos_semana && (
                          <p className="text-xs text-gray-600"><span className="font-medium text-green-700">Esta semana:</span> {p.hitos_semana}</p>
                        )}
                        {p.proximos_pasos && (
                          <p className="text-xs text-gray-500 mt-0.5"><span className="font-medium">Próximos:</span> {p.proximos_pasos}</p>
                        )}
                        {p.riesgos && p.riesgos !== "null" && (
                          <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                            <AlertTriangle size={10} /> {p.riesgos}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ocupación del equipo */}
          {(report.ocupacion_equipo || []).length > 0 && (
            <div>
              <SectionTitle color="text-blue-600">
                <Users size={12} className="text-blue-600" /> Ocupación del equipo
              </SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                {report.ocupacion_equipo.map((p, i) => {
                  const nivelColor = p.nivel === "alta" ? "text-red-600 bg-red-50" : p.nivel === "media" ? "text-amber-600 bg-amber-50" : "text-green-600 bg-green-50";
                  return (
                    <div key={p.persona || `ocu-${i}`} className="bg-white border border-gray-100 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-gray-800 truncate">{p.persona}</p>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${nivelColor}`}>{p.nivel}</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900">{(p.carga_horas || 0).toFixed(0)}h</p>
                      <p className="text-[10px] text-gray-400 truncate">{(p.proyectos || []).slice(0, 2).join(", ")}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Servicios a arrancar */}
          {(report.servicios_a_arrancar || []).length > 0 && (
            <div>
              <SectionTitle color="text-amber-600">
                <Truck size={12} className="text-amber-600" /> Servicios a arrancar
              </SectionTitle>
              <div className="space-y-2">
                {report.servicios_a_arrancar.map((s, i) => (
                  <div key={s.servicio || `svc-${i}`} className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                    <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${trafficColor(s.prioridad)}`} />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{s.servicio}</p>
                      <p className="text-xs text-gray-600">{s.estado}</p>
                      {s.accion_requerida && (
                        <p className="text-xs text-amber-700 font-medium mt-0.5 flex items-center gap-1">
                          <ArrowRight size={10} /> {s.accion_requerida}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pipeline Ofertas */}
          {report.pipeline_ofertas && (
            <div>
              <SectionTitle color="text-purple-600">
                <FileBarChart size={12} className="text-purple-600" /> Pipeline de Ofertas
              </SectionTitle>
              <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
                <p className="text-sm font-semibold text-gray-900 mb-1">{report.pipeline_ofertas.total} ofertas en pipeline</p>
                <p className="text-xs text-gray-600 mb-2">{report.pipeline_ofertas.resumen}</p>
                {(report.pipeline_ofertas.destacadas || []).map((d, i) => (
                  <p key={`dest-${i}`} className="text-xs text-purple-700 flex items-start gap-1">
                    <span className="mt-0.5">•</span>{d}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Operaciones */}
          {report.operaciones && (
            <div>
              <SectionTitle color="text-gray-500">
                <Wrench size={12} /> Operaciones
              </SectionTitle>
              <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3 leading-relaxed">{report.operaciones}</p>
            </div>
          )}

          {/* Decisiones necesarias */}
          {(report.decisiones_necesarias || []).length > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4">
              <SectionTitle color="text-red-600">
                <AlertTriangle size={12} className="text-red-600" /> Decisiones necesarias del Comité
              </SectionTitle>
              <ul className="space-y-1.5">
                {report.decisiones_necesarias.map((d, i) => (
                  <li key={`dec-${i}`} className="text-sm text-red-700 flex items-start gap-1.5">
                    <Circle size={6} className="mt-1.5 flex-shrink-0 fill-red-400 text-red-400" />{d}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Próximos hitos */}
          {(report.proximos_hitos || []).length > 0 && (
            <div>
              <SectionTitle color="text-gray-500">
                <Calendar size={12} /> Próximos hitos
              </SectionTitle>
              <ul className="space-y-1">
                {report.proximos_hitos.map((h, i) => (
                  <li key={`hito-report-${i}`} className="text-sm text-gray-600 flex items-start gap-1.5">
                    <ArrowRight size={12} className="mt-0.5 text-[#007934] flex-shrink-0" />{h}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────── */
export default function Committee() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "services");
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECS);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [aiHoursLoading, setAiHoursLoading] = useState(false);
  // Iter313 · O07 — notas manuales con % obsoleto frente al checklist real
  const [staleNotes, setStaleNotes] = useState([]);
  const [relativeDateIssues, setRelativeDateIssues] = useState([]);
  const [showStalePanel, setShowStalePanel] = useState(false);
  const [showRelDatePanel, setShowRelDatePanel] = useState(false);
  // Jev (roadmap-valoriza-jev-2026-10-11) · notas redundantes/duplicadas
  const [redundantNotes, setRedundantNotes] = useState([]);
  const [showRedundantPanel, setShowRedundantPanel] = useState(false);
  const [cleaningKey, setCleaningKey] = useState(null);
  const countdownRef = useRef(null);

  const buildDateParams = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    return params.toString() ? `?${params.toString()}` : "";
  };

  const handleExportXlsx = async () => {
    setExportingXlsx(true);
    try {
      const response = await axios.get(`${API}/committee/export/xlsx${buildDateParams()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const cd = response.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : "committee_report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado correctamente");
    } catch { toast.error("Error al exportar Excel"); }
    finally { setExportingXlsx(false); }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/committee/export/pdf${buildDateParams()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const cd = response.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : "committee_report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado correctamente");
    } catch { toast.error("Error al exportar PDF"); }
    finally { setExportingPdf(false); }
  };

  const cleanupDuplicateNotes = async (group) => {
    if (!window.confirm(
      `⚠ Vas a borrar ${group.count - 1} copia(s) de "${group.title}" (${group.key}), conservando solo la más reciente.\n\nEsta acción no se puede deshacer. ¿Confirmas?`
    )) return;
    setCleaningKey(group.type + group.key + group.title);
    try {
      const { data } = await axios.post(`${API}/committee/notes/cleanup-duplicates`, { note_ids: group.note_ids });
      toast.success(`${data.deleted_count} copia(s) eliminada(s)`);
      setRedundantNotes(prev => prev.filter(r => r !== group));
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo limpiar");
    } finally { setCleaningKey(null); }
  };

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data: d } = await axios.get(`${API}/committee/overview${qs}`);
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  const resetCountdown = useCallback(() => {
    setCountdown(AUTO_REFRESH_SECS);
  }, []);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    load();
    // fetch last sync status
    axios.get(`${API}/committee/sync/status`).then(r => setLastSync(r.data)).catch(() => {});
    // Try to load last weekly report
    axios.get(`${API}/committee/weekly-report/last`).then(r => { if (r.data?.semana) setWeeklyReport(r.data); }).catch(() => {});
    axios.get(`${API}/committee/notes/staleness-check`).then(r => {
      setStaleNotes(r.data.stale_notes || []);
      setRelativeDateIssues(r.data.relative_date_issues || []);
    }).catch(() => {});
    axios.get(`${API}/committee/notes/redundant-check`).then(r => {
      setRedundantNotes(r.data.redundant || []);
    }).catch(() => {});
  }, [load]);

  useEffect(() => {
    countdownRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          load();
          return AUTO_REFRESH_SECS;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: syncData } = await axios.post(`${API}/committee/sync`);
      toast.success(syncData.message || "Datos sincronizados");
      resetCountdown();
      await load();
      const { data: status } = await axios.get(`${API}/committee/sync/status`);
      setLastSync(status);
    } catch { toast.error("Error al sincronizar"); }
    finally { setSyncing(false); }
  };

  const handleAIHours = async () => {
    setAiHoursLoading(true);
    try {
      const { data: res } = await axios.post(`${API}/committee/ai/estimate-workitem-hours`);
      if (res.updated > 0) {
        toast.success(`Pando estimó horas para ${res.updated} work items`);
        await load();
      } else {
        toast.info(res.message || "Todos los work items ya tienen horas");
      }
    } catch (e) {
      toast.error("Error en estimación Pando de horas");
    } finally {
      setAiHoursLoading(false);
    }
  };

  const handleWeeklyReport = async () => {
    setWeeklyReportLoading(true);
    try {
      const { data: report } = await axios.post(`${API}/committee/ai/weekly-report`);
      setWeeklyReport(report);
      setShowWeeklyReport(true);
      toast.success("Informe semanal generado");
    } catch (e) {
      toast.error("Error al generar informe Pando");
    } finally {
      setWeeklyReportLoading(false);
    }
  };

  const fmtLastSync = () => {
    if (!lastSync?.finished_at && !lastSync?.started_at) return null;
    const dt = new Date(lastSync.finished_at || lastSync.started_at);
    return dt.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 rounded loading-shimmer" />
        <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl loading-shimmer" />)}</div>
        <div className="h-96 rounded-xl loading-shimmer" />
      </div>
    );
  }

  const { offers = [], projects = [], people = [], avg_rate = 65, avg_rate_is_fallback = false, kpis = {}, commercial_news = [], commercial_news_antiguas = [], people_period_days = 10 } = data || {};

  // Iter313 · O07 — agrupar por proyecto afectado y quedarnos con la nota
  // más reciente, para no listar 10 notas históricas del mismo caso.
  const staleByProject = Object.values(
    staleNotes.reduce((acc, s) => {
      const k = s.checklist_titulo;
      if (!acc[k] || new Date(s.note_updated_at) > new Date(acc[k].note_updated_at)) acc[k] = s;
      return acc;
    }, {})
  ).sort((a, b) => b.diff - a.diff);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Committee Reporting</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Control de proyectos activos, prelicitación y asignación de recursos
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Auto-refresh indicator */}
          <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-white border border-gray-100 rounded-lg px-2.5 py-1.5">
            <Clock size={11} className="text-gray-300" />
            <span>Auto en {formatCountdown(countdown)}</span>
          </div>
          {/* Last sync */}
          {fmtLastSync() && (
            <div className="text-xs text-gray-400 hidden md:block">
              Últ. sync: {fmtLastSync()}
            </div>
          )}
          {/* Sync button */}
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            data-testid="sync-btn"
          >
            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Sincronizando..." : "Sincronizar"}
          </button>
          {/* Weekly Report AI */}
          <button
            onClick={weeklyReport && !weeklyReportLoading ? () => setShowWeeklyReport(true) : handleWeeklyReport}
            disabled={weeklyReportLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            data-testid="weekly-report-btn"
          >
            {weeklyReportLoading ? <Loader2 size={13} className="animate-spin" /> : <BrainCircuit size={13} />}
            {weeklyReportLoading ? "Generando..." : weeklyReport ? "Ver Informe" : "Informe Semanal Pando"}
          </button>
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(type, val) => type === "from" ? setDateFrom(val) : setDateTo(val)}
            onClear={() => { setDateFrom(""); setDateTo(""); }}
          />
          <button
            onClick={handleExportXlsx}
            disabled={exportingXlsx}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#007934] bg-white border border-[#007934] rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
            data-testid="export-xlsx-btn"
          >
            {exportingXlsx ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exportingXlsx ? "Exportando..." : "Excel"}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={exportingPdf}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[#007934] rounded-lg hover:bg-[#006528] transition-colors disabled:opacity-50"
            data-testid="export-pdf-btn"
          >
            {exportingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exportingPdf ? "Generando..." : "PDF"}
          </button>
        </div>
      </div>

      {/* Iter313 · O07 — notas (manuales o de Pando, R14 amplió a ambas) con
          % desactualizado frente al checklist real (no se sobrescriben
          automáticamente: sólo se marcan para que alguien las revise). */}
      {staleByProject.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl" data-testid="stale-notes-banner">
          <button onClick={() => setShowStalePanel(s => !s)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="text-sm text-amber-800 font-medium flex items-center gap-2">
              <AlertTriangle size={15} className="text-amber-600" />
              {staleByProject.length} nota{staleByProject.length === 1 ? "" : "s"} con % desactualizado frente al checklist real — revisar vigencia
            </span>
            <ChevronDown size={14} className={`text-amber-500 transition-transform ${showStalePanel ? "rotate-180" : ""}`} />
          </button>
          {showStalePanel && (
            <div className="px-4 pb-3 space-y-1.5">
              {staleByProject.map(s => (
                <div key={s.note_id} className="text-xs text-amber-700 bg-white/60 rounded-lg px-3 py-2" data-testid={`stale-note-${s.note_id}`}>
                  <b>{s.checklist_titulo}</b>: la nota ({s.note_type === "ia_generated" ? "Pando" : "manual"}) dice <b>{s.note_pct}%</b> pero el checklist real está al <b>{s.actual_pct}%</b> (diferencia de {s.diff} puntos)
                  <div className="text-amber-500 mt-0.5 truncate">&quot;{s.note_title}&quot;</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* R14 (auditoría externa) · "VaxTor no conserva 35 días como plazo
          vigente tras el 30 de septiembre" — notas que dan una cuenta de
          días/semanas restantes junto a una fecha que YA pasó. */}
      {relativeDateIssues.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl" data-testid="relative-date-issues-banner">
          <button onClick={() => setShowRelDatePanel(s => !s)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="text-sm text-rose-800 font-medium flex items-center gap-2">
              <AlertTriangle size={15} className="text-rose-600" />
              {relativeDateIssues.length} nota{relativeDateIssues.length === 1 ? "" : "s"} con una cuenta de días/semanas que ya venció — la fecha referenciada ya pasó
            </span>
            <ChevronDown size={14} className={`text-rose-500 transition-transform ${showRelDatePanel ? "rotate-180" : ""}`} />
          </button>
          {showRelDatePanel && (
            <div className="px-4 pb-3 space-y-1.5">
              {relativeDateIssues.map(s => (
                <div key={s.note_id} className="text-xs text-rose-700 bg-white/60 rounded-lg px-3 py-2" data-testid={`relative-date-issue-${s.note_id}`}>
                  Dice <b>&quot;{s.relative_phrase}&quot;</b> referenciando el <b>{s.referenced_date}</b> — esa fecha pasó hace <b>{s.days_since_passed} días</b>, así que el plazo ya no es vigente.
                  <div className="text-rose-500 mt-0.5 truncate">&quot;{s.note_title}&quot; ({s.note_type === "ia_generated" ? "Pando" : "manual"})</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Jev (roadmap-valoriza-jev-2026-10-11) · notas redundantes/duplicadas
          por persona/proyecto — título idéntico (limpieza con 1 clic,
          conserva la más reciente) o, según Jev, misma información
          redactada distinto (revisión manual, sin acción automática). */}
      {redundantNotes.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl" data-testid="redundant-notes-banner">
          <button onClick={() => setShowRedundantPanel(s => !s)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="text-sm text-indigo-800 font-medium flex items-center gap-2">
              <Sparkles size={15} className="text-indigo-600" />
              {redundantNotes.length} grupo{redundantNotes.length === 1 ? "" : "s"} de notas posiblemente redundantes
            </span>
            <ChevronDown size={14} className={`text-indigo-500 transition-transform ${showRedundantPanel ? "rotate-180" : ""}`} />
          </button>
          {showRedundantPanel && (
            <div className="px-4 pb-3 space-y-1.5">
              {redundantNotes.map((r, i) => {
                const key = r.type + r.key + (r.title || r.title_a);
                return (
                  <div key={i} className="text-xs text-indigo-700 bg-white/60 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`redundant-note-${i}`}>
                    <div>
                      {r.match_basis === "titulo_exacto" ? (
                        <><b>{r.count}</b> copias idénticas de <b>&quot;{r.title}&quot;</b> — {r.key}</>
                      ) : (
                        <>Jev ({Math.round(r.confidence * 100)}%): <b>&quot;{r.title_a}&quot;</b> ≈ <b>&quot;{r.title_b}&quot;</b> — {r.key}</>
                      )}
                    </div>
                    {r.match_basis === "titulo_exacto" && (
                      <button onClick={() => cleanupDuplicateNotes(r)} disabled={cleaningKey === key}
                        data-testid={`redundant-note-cleanup-${i}`}
                        className="text-[10px] font-semibold text-white px-2.5 py-1 rounded-lg shrink-0 disabled:opacity-60" style={{ background: "#007934" }}>
                        {cleaningKey === key ? <Loader2 size={10} className="animate-spin inline" /> : "Limpiar"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex bg-white rounded-xl border border-gray-100 p-1 w-fit gap-1 flex-wrap">
        {[
          { key: "services", icon: Newspaper, label: "Servicios a arrancar", count: commercial_news.length },
          { key: "projects", icon: BarChart2, label: "Hoja de Control", count: projects.length + offers.length },
          { key: "ops", icon: Wrench, label: "Operaciones", count: people.filter(p => p.department === "Operaciones" || ["Jorge Tosca Ginel","Enrique Torres Sanz","Carlos Manuel Gil Lopez"].includes(p.name)).length },
          { key: "people", icon: Users, label: "Asignación de Personas", count: kpis.total_people || 0 },
        ].map(({ key, icon: Icon, label, count }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === key ? "bg-[#007934] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            data-testid={`tab-${key}`}>
            <Icon size={14} />
            {label}
            {count > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === key ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>{count}</span>}
          </button>
        ))}
      </div>

      {tab === "services" && <ServiciosArrancarTab commercialNews={commercial_news} commercialNewsAntiguas={commercial_news_antiguas} onRefresh={load} />}
      {tab === "projects" && (
        <ProjectControlTab
          offers={offers} projects={projects} avgRate={avg_rate} avgRateIsFallback={avg_rate_is_fallback}
          onRefresh={load} onEditProject={setEditProject}
        />
      )}
      {tab === "ops" && <OpsTeamSection people={people} onRefresh={load} />}
      {tab === "people" && <PeopleAssignmentTab people={people} periodDays={people_period_days} dateFrom={dateFrom} dateTo={dateTo} onAIHours={handleAIHours} aiHoursLoading={aiHoursLoading} />}

      {/* Project edit modal */}
      {editProject && (
        <ProjectEditModal
          project={editProject}
          onSave={() => { setEditProject(null); load(); }}
          onClose={() => setEditProject(null)}
        />
      )}

      {/* Weekly report panel */}
      {showWeeklyReport && weeklyReport && (
        <WeeklyReportPanel report={weeklyReport} onClose={() => setShowWeeklyReport(false)} />
      )}
    </div>
  );
}

